import type { IDurableJob } from '@condo/domain';
import { DurableJobStatus } from '@condo/domain';
import type { LucideIcon } from 'lucide-react';
import { Download, ExternalLink, FileSpreadsheet, FileText, FolderDown, FolderOpen, Table } from 'lucide-react';
import type { NavigateFunction } from 'react-router-dom';
import { downloadBlobFromUrl } from '@/shared/utils/downloadBlob';
import { toast } from '@/shared/utils/toast';
import { executeDownloadDocumentFolderZip } from './executeDownloadDocumentFolderZip';

/** Inject document utilities needed by tray actions. Call `configureDurableJobTrayActions` at app init. */
let _getSignedReadUrl: ((url: string) => Promise<{ url: string }>) | null = null;

export function configureDurableJobTrayActions(opts: { getSignedReadUrl: (url: string) => Promise<{ url: string }> }): void {
    _getSignedReadUrl = opts.getSignedReadUrl;
}

export interface DurableJobTrayAction {
    id: string;
    label: string;
    icon: LucideIcon;
    confirmation?: DurableJobTrayActionConfirmation;
    visible: (job: IDurableJob) => boolean;
    run: (job: IDurableJob, navigate: NavigateFunction) => void | Promise<void>;
}

export interface DurableJobTrayActionConfirmation {
    title: string;
    description: string;
    confirmLabel: string;
    cancelLabel: string;
}

export const DURABLE_JOB_TRAY_ACTIONS: DurableJobTrayAction[] = [
    // Download document folder as ZIP
    {
        id: 'download-document-folder',
        label: 'Download folder',
        icon: FolderDown,
        visible: job => job.name === 'downloadDocumentFolder' && job.status === DurableJobStatus.success,
        run: job => executeDownloadDocumentFolderZip(job.id),
    },

    // Download sign-off envelopes as ZIP
    {
        id: 'download-sign-off-envelopes',
        label: 'Download zip',
        icon: FolderDown,
        visible: job => job.name === 'downloadSignOffEnvelopes' && job.status === DurableJobStatus.success,
        run: job => executeDownloadDocumentFolderZip(job.id),
    },

    // Export MFH Renovation Project Position Sheet
    {
        id: 'export-renovation-position-sheet',
        label: 'Open Sheet',
        icon: Table,
        visible: job => {
            if (job.name !== 'exportMfhRenovationProjectPositionSheet' || job.status !== DurableJobStatus.success) {
                return false;
            }
            const sheetUrl = (job.result?.data as { sheetUrl?: string } | undefined)?.sheetUrl;
            return !!sheetUrl;
        },
        run: async job => {
            const sheetUrl = (job.result?.data as { sheetUrl?: string } | undefined)?.sheetUrl;
            // Guaranteed to exist by visible check
            window.open(sheetUrl!, '_blank');
        },
    },

    // Download MFH Investment Case Sheet (XLSX)
    {
        id: 'download-investment-case-sheet',
        label: 'Open XLSX File',
        icon: FileSpreadsheet,
        confirmation: {
            title: 'Confirm',
            description:
                'Reneo uses Google Sheets for all investment calculations. Excel outputs may not match the results in Google Sheets. Do you want to continue?',
            confirmLabel: 'Yes',
            cancelLabel: 'No',
        },
        visible: job => {
            if (
                (job.name !== 'downloadExternalMFHInvestmentCase' && job.name !== 'downloadMfhInvestmentCaseSheet') ||
                job.status !== DurableJobStatus.success
            ) {
                return false;
            }
            const bucketUrl = (job.result?.data as { data?: { uploadedFile?: { bucketUrl?: string } } } | undefined)?.data?.uploadedFile?.bucketUrl;
            return !!bucketUrl;
        },
        run: async job => {
            const bucketUrl = (job.result?.data as { data?: { uploadedFile?: { bucketUrl?: string } } } | undefined)?.data?.uploadedFile?.bucketUrl;
            // Guaranteed to exist by visible check
            try {
                if (!_getSignedReadUrl) throw new Error('configureDurableJobTrayActions not called');
                const { url: signedUrl } = await _getSignedReadUrl(bucketUrl!);
                window.open(signedUrl, '_blank');
            } catch (error) {
                toast.error('Failed to download investment case sheet');
                console.error('Download error:', error);
            }
        },
    },

    // Transfer Recurrent Payments - Open Export Folder
    {
        id: 'open-transfer-payments-folder',
        label: 'Open Export Folder',
        icon: FolderOpen,
        visible: job => {
            if (job.name !== 'transferRecurrentPayments' || job.status !== DurableJobStatus.success) {
                return false;
            }
            const exportFolder = (job.result?.data as { exportFolder?: string } | undefined)?.exportFolder;
            return !!exportFolder;
        },
        run: async job => {
            const exportFolder = (job.result?.data as { exportFolder?: string } | undefined)?.exportFolder;
            // Guaranteed to exist by visible check
            window.open(exportFolder!, '_blank');
        },
    },

    // Export Sales Package / Create Selling Exposes - Open Export Folder
    {
        id: 'open-sales-package-folder',
        label: 'Open Export Folder',
        icon: FolderOpen,
        visible: job => {
            if ((job.name !== 'exportSalesPackageToDrive' && job.name !== 'createSalesPackageSellingExposes') || job.status !== DurableJobStatus.success) {
                return false;
            }
            const exportFolder = (job.result?.data as { salesPackageExportFolderLink?: string } | undefined)?.salesPackageExportFolderLink;
            return !!exportFolder;
        },
        run: async job => {
            const exportFolder = (job.result?.data as { salesPackageExportFolderLink?: string } | undefined)?.salesPackageExportFolderLink;
            // Guaranteed to exist by visible check
            window.open(exportFolder!, '_blank');
        },
    },

    // Create Selling Expose - Open Document
    {
        id: 'open-selling-expose-doc',
        label: 'Open Document',
        icon: ExternalLink,
        visible: job => {
            if (job.name !== 'createSellingExpose' || job.status !== DurableJobStatus.success) {
                return false;
            }
            const data = job.result?.data as { path?: string; link?: string } | undefined;
            return !!(data?.path ?? data?.link);
        },
        run: async job => {
            const data = job.result?.data as { path?: string; link?: string } | undefined;
            const docUrl = data?.path ?? data?.link;
            // Guaranteed to exist by visible check
            window.open(docUrl!, '_blank');
        },
    },

    // Create Selling Expose - Download Document
    {
        id: 'download-selling-expose',
        label: 'Download Document',
        icon: Download,
        visible: job => job.name === 'createSellingExpose' && job.status === DurableJobStatus.success,
        run: async job => {
            const data = job.result?.data as { path?: string; link?: string; name?: string } | undefined;
            const url = data?.path ?? data?.link;
            if (!url) {
                toast.error('Document URL not found');
                return;
            }
            try {
                await downloadBlobFromUrl(url, data?.name);
            } catch (error) {
                toast.error('Failed to download document');
                console.error('Download error:', error);
            }
        },
    },

    // Render Document - Open/Download based on type
    {
        id: 'open-rendered-document',
        label: 'Open Document',
        icon: FileText,
        visible: job => {
            if (job.name !== 'renderDocument' || job.status !== DurableJobStatus.success) {
                return false;
            }
            const mainDoc = (job.result?.data as { mainDoc?: string } | undefined)?.mainDoc;
            const files = (job.result?.data as { docResult?: { files?: Record<string, { link?: string; path?: string }> } } | undefined)?.docResult?.files;
            const mainDocFile = mainDoc && files ? files[mainDoc] : undefined;
            return !!mainDocFile && !!mainDoc && ['xls', 'doc', 'pdf'].includes(mainDoc);
        },
        run: async job => {
            const mainDoc = (job.result?.data as { mainDoc?: string } | undefined)?.mainDoc;
            const files = (job.result?.data as { docResult?: { files?: Record<string, { link?: string; path?: string }> } } | undefined)?.docResult?.files;
            const mainDocFile = mainDoc && files ? files[mainDoc] : undefined;

            if (!mainDocFile) {
                toast.error('Document not found');
                return;
            }

            const url = mainDocFile.link ?? mainDocFile.path;
            if (!url) {
                toast.error('Document URL not found');
                return;
            }

            window.open(url, '_blank');
        },
    },
    {
        id: 'open-mfh-construction-plan-comparison-report',
        label: 'Open Report',
        icon: FileText,
        visible: job => {
            return job.name === 'runMfhConstructionPlanComparisonJob' && job.status === DurableJobStatus.success;
        },
        run: async (job, navigate: NavigateFunction) => {
            const params = new URLSearchParams({ tab: 'compare', compareSub: 'ai' });
            if (job.params?.sourceMfhConstructionPlanVersionId != null) {
                params.set('compareWith', String(job.params.sourceMfhConstructionPlanVersionId));
            }
            if (job.params?.targetMfhConstructionPlanVersionId != null) {
                params.set('version', String(job.params.targetMfhConstructionPlanVersionId));
            }
            if (job.result?.data?.reportId != null) {
                params.set('view', String(job.result.data.reportId));
            }
            navigate(`/assets/${job.params?.assetId}/construction-plans/${job.params?.mfhConstructionPlanId}?${params.toString()}`);
        },
    },
];
